define((require) => {
    "use strict";

    var ChunkingCollectionView  = require("ui/common/ChunkingCollectionView"),
        SlimSpinnerView         = require("ui/common/SlimSpinnerView"),
        Backbone                = require("backbone"),
        Syncer                  = require("shared/services/sync/Syncer"),
        _                       = require("underscore"),
        $                       = require("jquery");

    /**
     * Computes the height of the specified container by iterating it's children, only looking at the visible ones.
     *
     * This is needed to support the desired behavior on the dashboard, since we keep the tapped views in the same container, toggling visibility.
     *
     * @param  {jQueryElement} $container The container element, <tt>jQuery</tt> wrapped.
     * @return {Number} The computed height.
     */
    function computeHeight($container) {
        var height = 0;

        $container.children().each(function() {
            var $element = $(this);

            if ($element.is(":visible")) {
                height += $element.outerHeight(true);
            }
        });

        return height;
    }

    var SpinnerContainer = Backbone.View.extend({

        className: "spinner-container",

        render: function() {
            this.$el.append(new SlimSpinnerView().render().el);

            return this;
        }

    });

    /**
     * A <tt>ChunkingCollectionView</tt> subclass that enables the classic lazy loading UX when scrolling to the bottom of the page/view.
     *
     * @cfg {ChunkingCollection} collection The <tt>ChunkingCollection</tt> instance.
     * @cfg {Number} horizontalBuffer       Additional horizontal space, i.e. the distance from the bottom of the scrollbar to the bottom of the
     *                                      page/view - deciding when to start fetching the next chuck.
     *
     * @cfg {jQueryElement} scrollTarget    Optional, the element firing the scroll events. If not defined, we'll ask the <tt>viewScope.scrollTarget</tt>.
     *
     * @class InfiniteScrollingCollectionView
     */
    return ChunkingCollectionView.extend({

        className: "infinite-container",

        /**
         * @type {Boolean}
         */
        _pending: false,

        /**
         * @type {Boolean}
         * True when the spinner is spinning, false otherwise.
         */
        _spinning: false,

        /**
         * @type {SpinnerContainer}
         */
        _spinner: null,

        /**
         * The element firing the scroll events.
         *
         * @type {jQueryElement}
         */
        _$scrollTarget: null,

        /**
         * The container element to append the spinner view,
         * generated by the optional options.spinnerContainer selector
         *
         * @type {jQueryElement}
         */
        _$spinnerContainer: null,

        initialize: function() {
            this._scrollHandler = this._scrollHandler.bind(this);
        },

        onShow: function() {
            this.__super();

            this._spinner = new SpinnerContainer();
            this._spinner.render();

            this._$spinnerContainer = this.spinnerContainer ? this.$el.find(this.spinnerContainer) : this._$spinnerContainer;

            this._$scrollTarget = _.result(this.options, "scrollTarget") || _.result(this.viewScope, "scrollTarget");
            this._listenForScrollEvents();

            this.listenTo(this.collection, {
                "sync:state": function() {
                    // Start spinner on sync, including if pending to avoid a "visual jump" on the dashboard
                    // between clearing the old content and adding this spinner, stop it on completion...
                    var complete = Syncer.State.complete(this.collection);

                    if (complete) {
                        if (this._spinning) {
                            this._spinning = false;
                            this._spinner.$el.detach();
                        }
                        this._pending = false;

                        this.triggerMethod("load:end");

                    // Start, on time only ("_pending" is used slightly different, not set to true via sync event, but
                    // via scroll)...
                    } else if (!this._spinning) {
                        this._spinning = true;

                        if (this._$spinnerContainer) {
                            this._$spinnerContainer.append(this._spinner.el);
                        } else {
                            this.$el.append(this._spinner.el);
                        }

                        this.triggerMethod("load:start");
                    }
                }
            });
        },

        /**
         * Whether the view is considered active (default) - when inactive, we won't listen for any scroll events.
         *
         * @param  {Boolean} active The current active state.
         */
        active: function(active) {
            if (active) {
                this._listenForScrollEvents();
            } else {
                this._disposeScrollListener();
            }
        },

        _listenForScrollEvents: function() {
            this._$scrollTarget.on("scroll", this._scrollHandler);
        },

        _disposeScrollListener: function() {
            this._$scrollTarget.off("scroll", this._scrollHandler);
        },

        /**
         * @private
         *
         * Handles scrolling.
         */
        _scrollHandler: function() {
            var horizontalBuffer = this.options.horizontalBuffer || 0,
                // TODO: only compute height when collection has changed?
                scrollTargetHeight = computeHeight(this._$scrollTarget),
                viewportHeight = this._$scrollTarget.height();

            // Sanity check
            if (scrollTargetHeight < viewportHeight) {
                return;
            }

            var threshold = (scrollTargetHeight - viewportHeight) - horizontalBuffer;

            // Fetch the next chuck when we reach the bottom...
            if (this._$scrollTarget.scrollTop() > threshold && !this._pending && !this.collection.isPending() && this.collection.hasMore()) {
                this._pending = true;
                this.collection.next(); // will start triggering sync events!
            }
        },

        /**
         * @inheritDoc
         */
        onClose: function() {
            this.__super();

            this._disposeScrollListener();
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/common/InfiniteScrollingCollectionView.js